import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, AlertCircle, CheckCircle } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export function NotificationWidget() {
  const [notificationPermission, setNotificationPermission] =
    useState<NotificationPermission>("default");
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    checkNotificationPermission();
  }, []);

  const checkNotificationPermission = () => {
    if ("Notification" in window) {
      setNotificationPermission(Notification.permission);
      // setNotificationPermission("denied");
      setIsChecking(false);
    } else {
      setIsChecking(false);
    }
  };

  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      alert("이 브라우저는 알림을 지원하지 않습니다.");
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === "granted") {
        console.log("알림 권한이 허용되었습니다.");
      } else if (permission === "denied") {
        console.warn("알림 권한이 거부되었습니다.");
      }
    } catch (error) {
      console.error("알림 권한 요청 실패:", error);
    }
  };

  const getButtonVariant = () => {
    if (!("Notification" in window)) return "outline";

    switch (notificationPermission) {
      case "granted":
        return "success";
      case "denied":
        return "destructive";
      case "default":
      default:
        return "warning";
    }
  };

  const getButtonIcon = () => {
    if (!("Notification" in window)) {
      return <AlertCircle className="h-4 w-4" />;
    }

    switch (notificationPermission) {
      case "granted":
        return <Bell className="h-4 w-4" />;
      case "denied":
        return <BellOff className="h-4 w-4" />;
      case "default":
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  //@ts-ignore
  const getButtonText = () => {
    if (!("Notification" in window)) return "알림 미지원";

    switch (notificationPermission) {
      case "granted":
        return "알림 허용됨";
      case "denied":
        return "알림 차단됨";
      case "default":
      default:
        return "알림 미설정";
    }
  };

  const renderNotificationStatus = () => {
    if (!("Notification" in window)) {
      return (
        <Card className="p-6 border-yellow-500/50 bg-yellow-500/10">
          <div className="flex items-start gap-4">
            <AlertCircle className="h-6 w-6 text-yellow-500 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold text-lg mb-2">알림 미지원</h3>
              <p className="text-sm text-muted-foreground mb-3">
                현재 브라우저는 알림 기능을 지원하지 않습니다.
              </p>
              <p className="text-sm text-muted-foreground">
                최신 버전의 Chrome, Firefox, Safari, Edge 브라우저를
                사용해주세요.
              </p>
            </div>
          </div>
        </Card>
      );
    }

    switch (notificationPermission) {
      case "granted":
        return (
          <Card className="p-6 border-green-500/50 bg-green-500/10">
            <div className="flex items-start gap-4">
              <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">알림 허용됨</h3>
                <p className="text-sm text-muted-foreground">
                  알림이 정상적으로 활성화되어 있습니다.
                </p>
              </div>
            </div>
          </Card>
        );

      case "denied":
        return (
          <Card className="p-6 border-red-500/50 bg-red-500/10">
            <div className="flex items-start gap-4">
              <BellOff className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">알림 차단됨</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  알림 권한이 차단되어 있습니다. 브라우저 설정에서 권한을
                  변경해주세요.
                </p>
                <div className="bg-background/50 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium">알림 허용 방법:</p>
                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>브라우저 주소창 왼쪽의 자물쇠 아이콘을 클릭합니다</li>
                    <li>"알림" 또는 "권한" 설정을 찾습니다</li>
                    <li>알림을 "허용"으로 변경합니다</li>
                    <li>페이지를 새로고침합니다</li>
                  </ol>
                </div>
              </div>
            </div>
          </Card>
        );

      case "default":
      default:
        return (
          <Card className="p-6 border-blue-500/50 bg-blue-500/10">
            <div className="flex items-start gap-4">
              <Bell className="h-6 w-6 text-blue-500 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">알림 허용 필요</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  중요한 메시지와 업데이트를 놓치지 않으려면 알림을
                  허용해주세요.
                </p>
                <Button
                  onClick={requestNotificationPermission}
                  className="w-full sm:w-auto"
                >
                  <Bell className="h-4 w-4 mr-2" />
                  알림 허용하기
                </Button>
                <div className="mt-4 bg-background/50 rounded-lg p-4">
                  <p className="text-sm font-medium mb-2">알림을 허용하면:</p>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>새로운 메시지를 즉시 받아볼 수 있습니다</li>
                    <li>친구 요청 및 응답을 실시간으로 확인할 수 있습니다</li>
                    <li>중요한 시스템 알림을 받을 수 있습니다</li>
                  </ul>
                </div>
              </div>
            </div>
          </Card>
        );
    }
  };

  if (isChecking) {
    return (
      <div className="flex items-center justify-center">
        <Button variant="outline" disabled>
          <Bell className="h-4 w-4 mr-2" />
          확인 중...
        </Button>
      </div>
    );
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="relative">
          <Button variant={getButtonVariant()} size="icon" className="gap-2">
            {getButtonIcon()}
            {/* {getButtonText()} */}
          </Button>
          {/* 레드닷 표시 - denied 또는 default 상태일 때 */}
          {(notificationPermission === "denied" ||
            notificationPermission === "default") && (
            <span className="absolute top-[-2px] right-[-2px] flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-90"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
            </span>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        {renderNotificationStatus()}
      </PopoverContent>
    </Popover>
  );
}
